import React, { useMemo } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import { Box, Link, Stack, Typography, useTheme } from '@mui/material';

import { useGetTourOfferSummaries, useGetTourOfferSummary } from '~/hooks/apis/tours/useTourOffers';

import { OfferShare } from './departureInventory';
import { pricingAndInventoryPath } from './pricingAndInventoryTab';

const LTLE_NAME_PREFIX = /^limited time offer:\s*/i;

/** Every LTLE is named "Limited Time Offer: …", which says nothing once cut short. */
export function ltleDisplayName(name: string): string {
  return name.replace(LTLE_NAME_PREFIX, 'LTLE: ');
}

/** The colour a share is marked in: the theme's primary for the tour itself, then one per LTLE. */
export function useShareColour(): (share: OfferShare) => string {
  const theme = useTheme();
  const ltleColours = [
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
  ];

  return (share) =>
    share.colourIndex === undefined ? theme.palette.primary.main : ltleColours[share.colourIndex % ltleColours.length];
}

/** Each LTLE's share labelled with its name, once loaded. */
export function useNamedShares(shares: Array<OfferShare>): Array<OfferShare> {
  const tourOfferIds = useMemo(() => shares.flatMap((share) => share.tourOfferId ?? []), [shares]);
  const summaries = useGetTourOfferSummaries(tourOfferIds);

  return useMemo(
    () =>
      shares.map((share) => {
        const summary = share.tourOfferId ? summaries.get(share.tourOfferId) : undefined;
        return summary?.name ? { ...share, label: ltleDisplayName(summary.name) } : share;
      }),
    [shares, summaries],
  );
}

/** True when anything on the departure was sold by an LTLE, so a split is worth showing. */
export function hasLinkedShare(shares: Array<OfferShare>): boolean {
  return shares.some((share) => !share.isOwn && share.units > 0);
}

/** A selling tour's colour and name, linked to an LTLE's own page, with its travellers when given. */
export function ShareLabel({ share, units }: { share: OfferShare; units?: number }) {
  const colourOf = useShareColour();
  const { data: summary } = useGetTourOfferSummary(share.tourOfferId ?? undefined);
  const label = summary?.name ? ltleDisplayName(summary.name) : share.label;

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
      <Box width={8} height={8} borderRadius="50%" bgcolor={colourOf(share)} flexShrink={0} />
      {summary?.legacyTourId ? (
        <Link
          component={RouterLink}
          to={pricingAndInventoryPath(summary.legacyTourId)}
          variant="caption"
          noWrap
          title={summary.name ?? label}
        >
          {label}
        </Link>
      ) : (
        <Typography variant="caption" noWrap title={label}>
          {label}
        </Typography>
      )}
      {units !== undefined && (
        <Typography variant="caption" color="text.secondary" flexShrink={0}>
          · {units}
        </Typography>
      )}
    </Stack>
  );
}

/** Each selling tour on its own line, with its travellers. */
export function ShareList({ shares }: { shares: Array<OfferShare> }) {
  return (
    <Stack spacing={0.25}>
      {shares
        .filter((share) => share.units !== 0)
        .map((share) => (
          <ShareLabel key={share.tourOfferId ?? 'own'} share={share} units={share.units} />
        ))}
    </Stack>
  );
}
